import type { StrapiSamplingScheduleRow } from '../../api/strapiCrm';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import {
  buildQuotationScheduleDaySummariesByDate,
  type QuotationScheduleDaySummary,
} from '../../components/surveyScheduleCalendar';
import { dynamicRelation, scheduleDocIdFromTeamRow } from '../../components/surveyScheduleCalendar/utils';
import {
  calendarDateFromStoredDateTime,
  calendarDateToUtcMidnightIso,
  isoDatesInclusive,
} from '../../sampling/surveyScheduleCalendar';
import { buildGroupLabel, groupIndexByCreationOrder, scheduleDisplayCustomerName } from './samplingSchedulePlans';

/** Normalisasi payload/JSON `customer_agreed_dates` → daftar `YYYY-MM-DD` unik terurut. */
export function normalizeCustomerAgreedCalendarDates(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out = new Set<string>();
  for (const item of value) {
    const raw = String(item ?? '').trim();
    if (!raw) continue;
    const date = /^\d{4}-\d{2}-\d{2}/.test(raw)
      ? raw.slice(0, 10)
      : calendarDateFromStoredDateTime(raw);
    if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) out.add(date);
  }
  return [...out].sort();
}

/**
 * Tanggal kesepakatan customer untuk kalender.
 * Prefer `customer_agreed_dates` (array eksplisit); fallback rentang legacy start/end.
 */
export function customerAgreedCalendarDatesFromSchedule(
  schedule: Pick<
    StrapiSamplingScheduleRow,
    'customer_agreed_dates' | 'customer_agreed_start_date' | 'customer_agreed_end_date'
  > | null | undefined,
): string[] {
  if (!schedule) return [];
  const fromArray = normalizeCustomerAgreedCalendarDates(schedule.customer_agreed_dates);
  if (fromArray.length > 0) return fromArray;

  const start = calendarDateFromStoredDateTime(schedule.customer_agreed_start_date);
  if (!start) return [];
  const finish = calendarDateFromStoredDateTime(schedule.customer_agreed_end_date) || start;
  return finish >= start ? isoDatesInclusive(start, finish) : [start];
}

/** Payload update schedule: array tanggal + min/max legacy untuk PDF / WO testing. */
export function buildCustomerAgreedDatePayload(dates: Iterable<string>): {
  customer_agreed_dates: string[];
  customer_agreed_start_date: string | null;
  customer_agreed_end_date: string | null;
} {
  const sorted = normalizeCustomerAgreedCalendarDates([...dates]);
  return {
    customer_agreed_dates: sorted,
    customer_agreed_start_date: sorted[0] ? calendarDateToUtcMidnightIso(sorted[0]) : null,
    customer_agreed_end_date: sorted.length
      ? calendarDateToUtcMidnightIso(sorted[sorted.length - 1])
      : null,
  };
}

/**
 * Sample diterima di kantor menandai work order, bukan satu baris jadwal.
 * Jadwal lain pada WO yang sama (reschedule, group) ikut dianggap sudah diterima.
 */
export function samplingReceivedAtByScheduleDocumentId(
  schedules: StrapiSamplingScheduleRow[],
): Map<string, string> {
  const receivedByWorkOrder = new Map<string, string>();
  for (const schedule of schedules) {
    const received = String(schedule.sampling_received_at ?? '').trim();
    const workOrderId = String(schedule.work_order?.documentId ?? '').trim();
    if (received && workOrderId && !receivedByWorkOrder.has(workOrderId)) {
      receivedByWorkOrder.set(workOrderId, received);
    }
  }

  const bySchedule = new Map<string, string>();
  for (const schedule of schedules) {
    const scheduleId = String(schedule.documentId ?? '').trim();
    if (!scheduleId) continue;
    const own = String(schedule.sampling_received_at ?? '').trim();
    const workOrderId = String(schedule.work_order?.documentId ?? '').trim();
    const inherited = workOrderId ? receivedByWorkOrder.get(workOrderId) ?? '' : '';
    const resolved = own || inherited;
    if (resolved) bySchedule.set(scheduleId, resolved);
  }
  return bySchedule;
}

function customerAddressFromSchedule(schedule: StrapiSamplingScheduleRow): string {
  const customer = schedule.quotation?.customer as
    | { address?: string | null; city?: string | null; province?: string | null }
    | null
    | undefined;
  if (!customer) return '';
  const parts = [
    String(customer.address ?? '').trim(),
    String(customer.city ?? '').trim(),
    String(customer.province ?? '').trim(),
  ].filter(Boolean);
  return parts.join(', ');
}

/** Metadata penjadwalan lengkap untuk baris tim survey (selaras dengan halaman detail). */
export function syntheticSamplingScheduleFromRow(
  schedule: StrapiSamplingScheduleRow,
  samplingReceivedAtOverride?: string,
): Record<string, unknown> {
  const customer = schedule.quotation?.customer;
  const addressParts = customerAddressFromSchedule(schedule).split(',').map((s) => s.trim());
  return {
    documentId: schedule.documentId,
    schedule_number: schedule.schedule_number ?? '',
    progress_status: schedule.progress_status ?? '',
    notes: schedule.notes ?? '',
    transportasi_note: schedule.transportasi_note ?? '',
    sampling_received_at:
      samplingReceivedAtOverride?.trim() || schedule.sampling_received_at || '',
    work_order: schedule.work_order
      ? {
          documentId: schedule.work_order.documentId,
          job_number: schedule.work_order.job_number ?? '',
          progress_status: schedule.work_order.progress_status ?? '',
        }
      : null,
    quotation_group: schedule.quotation_group ?? null,
    ...(schedule.quotation_recurring_event
      ? { quotation_recurring_event: schedule.quotation_recurring_event }
      : {}),
    sampling_plans: [],
    quotation: schedule.quotation
      ? {
          documentId: schedule.quotation.documentId,
          quotation_number: schedule.quotation.quotation_number ?? '',
          is_contract: schedule.quotation.is_contract ?? false,
          is_consultant_quote: schedule.quotation.is_consultant_quote ?? false,
          contract_period_start_date: schedule.quotation.contract_period_start_date ?? null,
          contract_period_end_date: schedule.quotation.contract_period_end_date ?? null,
          customer: {
            name: customer?.name ?? '',
            address: addressParts[0] ?? '',
            city: addressParts[1] ?? '',
            province: addressParts[2] ?? '',
          },
        }
      : null,
  };
}

/** Gabungkan metadata penjadwalan dari daftar schedule ke relasi tim survey. */
export function enrichSurveyTeamRowsWithSchedules(
  teams: DynamicEntityRow[],
  schedules: StrapiSamplingScheduleRow[],
): DynamicEntityRow[] {
  if (!schedules.length) return teams;
  const byId = new Map(schedules.map((row) => [row.documentId.trim(), row]));
  const receivedBySchedule = samplingReceivedAtByScheduleDocumentId(schedules);
  return teams.map((team) => {
    const scheduleDocId = scheduleDocIdFromTeamRow(team);
    const scheduleRow = scheduleDocId ? byId.get(scheduleDocId) : undefined;
    if (!scheduleRow) return team;

    const synthetic = syntheticSamplingScheduleFromRow(
      scheduleRow,
      receivedBySchedule.get(scheduleDocId),
    );
    const directSchedule = dynamicRelation(team, 'sampling_schedule');
    if (directSchedule) {
      return {
        ...team,
        sampling_schedule: {
          ...directSchedule,
          ...synthetic,
        },
      } as DynamicEntityRow;
    }

    const workOrder = dynamicRelation(team, 'work_order');
    if (workOrder) {
      const woSchedule = dynamicRelation(workOrder as DynamicEntityRow, 'sampling_schedule');
      return {
        ...team,
        work_order: {
          ...workOrder,
          sampling_schedule: {
            ...(woSchedule ?? {}),
            ...synthetic,
          },
        },
      } as DynamicEntityRow;
    }

    return { ...team, sampling_schedule: synthetic } as DynamicEntityRow;
  });
}

/** Tanggal yang sudah punya petugas, per jadwal. */
export function occupiedDatesByScheduleDocumentId(
  teams: DynamicEntityRow[],
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const [date, summaries] of buildQuotationScheduleDaySummariesByDate(teams)) {
    for (const summary of summaries) {
      const scheduleId = summary.scheduleDocumentId.trim();
      if (!scheduleId) continue;
      const dates = map.get(scheduleId) ?? new Set<string>();
      dates.add(date);
      map.set(scheduleId, dates);
    }
  }
  return map;
}

/** Schedule yang sudah punya slot tim survey (berdasarkan tanggal di `scheduled_date`). */
export function scheduleDocumentIdsWithTeamSlots(teams: DynamicEntityRow[]): Set<string> {
  return new Set(occupiedDatesByScheduleDocumentId(teams).keys());
}

/**
 * Kartu kalender untuk tanggal kesepakatan yang belum punya petugas.
 * `customer_agreed_dates` tetap digambar meski tanggal lain pada jadwal yang sama sudah ada tim.
 * Rentang legacy start/end tidak diisi ulang bila jadwal itu sudah punya slot tim.
 */
export function buildDateOnlyScheduleSummariesByDate(
  schedules: StrapiSamplingScheduleRow[],
  occupiedDatesBySchedule: Map<string, Set<string>>,
): Map<string, QuotationScheduleDaySummary[]> {
  const map = new Map<string, QuotationScheduleDaySummary[]>();
  const receivedBySchedule = samplingReceivedAtByScheduleDocumentId(schedules);
  const groupsByQuotation = new Map<
    string,
    Array<{ documentId?: string | null; createdAt?: string | null }>
  >();
  for (const schedule of schedules) {
    if (!schedule.quotation?.is_contract) continue;
    const quotationId = schedule.quotation.documentId?.trim() ?? '';
    const groupId = schedule.quotation_group?.documentId?.trim() ?? '';
    if (!quotationId || !groupId) continue;
    const list = groupsByQuotation.get(quotationId) ?? [];
    list.push({
      documentId: groupId,
      createdAt: schedule.quotation_group?.createdAt ?? null,
    });
    groupsByQuotation.set(quotationId, list);
  }
  const contractGroupIndexByQuotation = new Map<string, Map<string, number>>();
  for (const [quotationId, groups] of groupsByQuotation) {
    contractGroupIndexByQuotation.set(quotationId, groupIndexByCreationOrder(groups));
  }
  schedules.forEach((schedule) => {
    const scheduleId = schedule.documentId?.trim();
    if (!scheduleId) return;

    const occupied = occupiedDatesBySchedule.get(scheduleId);
    const explicitDates = normalizeCustomerAgreedCalendarDates(schedule.customer_agreed_dates);
    if ((occupied?.size ?? 0) > 0 && explicitDates.length === 0) return;

    const dates =
      explicitDates.length > 0
        ? explicitDates
        : customerAgreedCalendarDatesFromSchedule(schedule);
    const openDates = dates.filter((date) => !occupied?.has(date));
    if (!openDates.length) return;

    const isContract = Boolean(schedule.quotation?.is_contract);
    const quotationId = schedule.quotation?.documentId?.trim() ?? '';
    const groupId = schedule.quotation_group?.documentId?.trim() ?? '';
    const groupIndex =
      isContract && quotationId && groupId
        ? (contractGroupIndexByQuotation.get(quotationId)?.get(groupId) ?? 0)
        : 0;
    const groupLabel =
      isContract && schedule.quotation_group
        ? buildGroupLabel(
            {
              recurring_type: schedule.quotation_group.recurring_type,
              recurring_interval: schedule.quotation_group.recurring_interval,
              recurring_count_override: schedule.quotation_group.recurring_count_override,
              recurring_start_date: schedule.quotation_group.recurring_start_date,
            },
            groupIndex,
            {
              is_contract: true,
              is_consultant_quote: Boolean(schedule.quotation?.is_consultant_quote),
              contract_period_start_date: schedule.quotation?.contract_period_start_date ?? null,
              contract_period_end_date: schedule.quotation?.contract_period_end_date ?? null,
            },
          )
        : null;
    const eventIndex = schedule.quotation_recurring_event?.event_index;
    const summary: QuotationScheduleDaySummary = {
      quotationDocumentId: schedule.quotation?.documentId?.trim() ?? '',
      quotationNumber: schedule.quotation?.quotation_number?.trim() || 'Quotation',
      scheduleDocumentId: scheduleId,
      scheduleNumber: schedule.schedule_number?.trim() || scheduleId,
      scheduleStatus: schedule.progress_status?.trim() || 'scheduling_in_progress',
      customerName: scheduleDisplayCustomerName(schedule) || '—',
      address: customerAddressFromSchedule(schedule),
      notes: schedule.notes?.trim() ?? '',
      transportasiNote: schedule.transportasi_note?.trim() ?? '',
      measurementTypesLabel: '',
      samplingReceivedAt:
        receivedBySchedule.get(scheduleId) ?? schedule.sampling_received_at?.trim() ?? '',
      workOrderDocumentId: schedule.work_order?.documentId?.trim() ?? '',
      workOrderJobNumber: schedule.work_order?.job_number?.trim() ?? '',
      beritaAcaraAllIssued: false,
      teamLeadName: '—',
      teamCount: 0,
      members: [],
      teamName: null,
      groupLabel,
      isContract,
      recurringEventIndex: eventIndex != null && Number.isFinite(eventIndex) ? eventIndex : null,
    };

    openDates.forEach((date) => {
      map.set(date, [...(map.get(date) ?? []), summary]);
    });
  });
  return map;
}

export function buildSamplingScheduleCalendarTeams(
  teams: DynamicEntityRow[],
  schedules: StrapiSamplingScheduleRow[],
): {
  calendarTeams: DynamicEntityRow[];
  dateOnlySummariesByDate: Map<string, QuotationScheduleDaySummary[]>;
} {
  const calendarTeams = enrichSurveyTeamRowsWithSchedules(teams, schedules);
  const dateOnlySummariesByDate = buildDateOnlyScheduleSummariesByDate(
    schedules,
    occupiedDatesByScheduleDocumentId(calendarTeams),
  );
  return { calendarTeams, dateOnlySummariesByDate };
}

function removeScheduleFromDateOnlyMap(
  map: Map<string, QuotationScheduleDaySummary[]>,
  scheduleDocumentId: string,
): Map<string, QuotationScheduleDaySummary[]> {
  const next = new Map<string, QuotationScheduleDaySummary[]>();
  for (const [date, summaries] of map.entries()) {
    const filtered = summaries.filter((row) => row.scheduleDocumentId !== scheduleDocumentId);
    if (filtered.length > 0) next.set(date, filtered);
  }
  return next;
}

export type DetailScheduleCalendarOverride = {
  scheduleDocumentId: string;
  hasTeamAssignments: boolean;
  replacementTeams: DynamicEntityRow[];
  localDateOnlySummary?: QuotationScheduleDaySummary | null;
  localDateOnlyDates?: string[];
  /** Ganti tim jadwal ini meski replacement kosong (setelah hapus tanggal lokal). */
  applyEmptyReplacement?: boolean;
};

/** Kalender detail: basis global + override penjadwalan yang sedang dibuka. */
export function buildDetailPageScheduleCalendar(
  allSurveyTeams: DynamicEntityRow[],
  allSchedules: StrapiSamplingScheduleRow[],
  override: DetailScheduleCalendarOverride,
): {
  calendarTeams: DynamicEntityRow[];
  dateOnlySummariesByDate: Map<string, QuotationScheduleDaySummary[]>;
} {
  const scheduleId = override.scheduleDocumentId.trim();
  const base = buildSamplingScheduleCalendarTeams(allSurveyTeams, allSchedules);

  const applyLocalDateOnly = (
    dateOnlySummariesByDate: Map<string, QuotationScheduleDaySummary[]>,
  ): Map<string, QuotationScheduleDaySummary[]> => {
    // Setelah draft siap, localDateOnlyDates adalah sumber kebenaran kartu kosong jadwal ini
    // (termasuk hybrid: sebagian tanggal punya tim, sebagian masih kosong).
    if (override.applyEmptyReplacement && override.localDateOnlyDates) {
      const cleared = removeScheduleFromDateOnlyMap(dateOnlySummariesByDate, scheduleId);
      if (!override.localDateOnlySummary || override.localDateOnlyDates.length === 0) {
        return cleared;
      }
      const next = new Map(cleared);
      for (const date of override.localDateOnlyDates) {
        const existing = next.get(date) ?? [];
        next.set(date, [...existing, override.localDateOnlySummary]);
      }
      return next;
    }
    if (override.localDateOnlySummary && override.localDateOnlyDates?.length) {
      const cleared = removeScheduleFromDateOnlyMap(dateOnlySummariesByDate, scheduleId);
      const next = new Map(cleared);
      for (const date of override.localDateOnlyDates) {
        const existing = next.get(date) ?? [];
        next.set(date, [...existing, override.localDateOnlySummary]);
      }
      return next;
    }
    return dateOnlySummariesByDate;
  };

  if (override.replacementTeams.length === 0 && !override.applyEmptyReplacement) {
    return {
      calendarTeams: base.calendarTeams,
      dateOnlySummariesByDate: applyLocalDateOnly(base.dateOnlySummariesByDate),
    };
  }

  const calendarTeams = [
    ...base.calendarTeams.filter((row) => scheduleDocIdFromTeamRow(row) !== scheduleId),
    ...override.replacementTeams,
  ];

  const rebuilt = buildSamplingScheduleCalendarTeams(calendarTeams, allSchedules);
  return {
    calendarTeams,
    dateOnlySummariesByDate: applyLocalDateOnly(rebuilt.dateOnlySummariesByDate),
  };
}
